import { FormattedMessage, useIntl } from 'react-intl';
import { DataResult } from 'models/data';
import { getChartColorWithMax } from 'components/chart/data/ChartColors';
import { getLabelForDatapoint } from 'pages/insights-pro/graphs/graphUtils';
import { MeasureComparison } from 'components/MeasureComparison';
import { MeasureUnitCode } from 'components/measure-unit/MeasureUnitCode';
import { findIconForCategory } from 'pages/insights-pro/common/icons/CategoryIcons';
import { Service } from 'services/data.service';
import { OverTimeCommonData, OverTimeReport } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeReport';
import { Tile } from 'components/Layout/PageContainer';
import { useModelService } from 'services/model/model.service';
import { OverTimeReportInfo } from 'pages/insights-pro/common/reports/over-time-chart/models';
import { OverTimeReportMetrics } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeReportMetrics';
import { useEffect } from 'react';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { OverTimeChart } from 'pages/insights-pro/common/reports/over-time-chart/OverTimeChart';
import { CategoriesOverTimeReportInfoContent } from './CategoriesOverTimeReportInfoContent';

export function CategoriesOverTimeReport() {
  const intl = useIntl();
  const { setContent } = useInfoDrawer();

  const { getMeasureByMetric, getAttribute } = useModelService();

  const prdCategory = getAttribute('prdCategory');
  const ordGmvVal = getMeasureByMetric('ordGmvVal', 'OD');

  useEffect(() => {
    const infoContent = {
      titleKey: 'insights.category.reports.over_time',
      subtitleKey: 'info.content.all_dates_utc',
      content: <CategoriesOverTimeReportInfoContent />,
    };

    setContent(infoContent);

    return () => {
      setContent(null);
    };
  }, [setContent, ordGmvVal]);

  const extractCategoryInfo = (data: DataResult) => {
    const categories = data.sets.categories.map(entry => {
      const categoryName = prdCategory.getValue(entry.attributes);
      return {
        name: categoryName,
        code: prdCategory.getRawValue(entry.attributes),
        totalGmv: ordGmvVal.getValue(entry.values),
        compareGmv: ordGmvVal.getValue(entry.compareValues),
        color: '',
        isOther: false,
        timeInfos: entry.sets.timeSeries.map(timeEntry => ({
          code: getLabelForDatapoint(timeEntry, intl),
          gmv: ordGmvVal.getValue(timeEntry.values),
          comparisonGmv: ordGmvVal.getValue(timeEntry.compareValues),
        })),
        rank: 0,
        compareRank: 0,
      };
    });

    const sortedByCurrent = [...categories]
      .sort((a, b) => b.totalGmv - a.totalGmv)
      .map((cat, idx) => ({ ...cat, rank: idx + 1 }));

    const sortedByCompare = [...sortedByCurrent]
      .sort((a, b) => b.compareGmv - a.compareGmv)
      .map((cat, idx) => ({ ...cat, compareRank: idx + 1 }));

    const finalSorted = [...sortedByCompare].sort((a, b) => b.totalGmv - a.totalGmv);

    return finalSorted.map((cat, index) => ({
      ...cat,
      color: getChartColorWithMax(index, 5),
      isOther: index >= 5,
      rank: index + 1,
    }));
  };
  const extractTotals = (data: DataResult) => ({
    totalGmv: ordGmvVal.getValue(data.values),
    compareGmv: ordGmvVal.getValue(data.compareValues),
  });

  const renderCategoryRow = (category: CategoryOverTimeReportInfo, commonData: OverTimeCommonData) => {
    const currentVal = category.totalGmv ?? 0;
    const compareVal = category.compareGmv ?? 0;

    const { marketShare, compareShare, growth } = OverTimeReportMetrics(
      currentVal,
      compareVal,
      commonData.totalGmvAllItems,
      commonData.totalCompareGmvAllItems
    );

    return (
      <div key={category.code} className="w-full bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="pl-2 pr-2">{findIconForCategory(category.code, 24)}</div>
          <span style={{ fontSize: '20px', fontWeight: 'bold' }}>{category.name}</span>
        </div>

        <div className="grid grid-cols-4 divide-x divide-gray-200 p-4">
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">{ordGmvVal.metric.name}</span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">
                {Intl.NumberFormat('en', {
                  notation: 'compact',
                  maximumFractionDigits: 2,
                }).format(currentVal)}
              </span>
              {commonData.isComparisonActive && compareVal !== undefined && (
                <MeasureComparison
                  value={currentVal}
                  comparisonValue={compareVal}
                  unit={MeasureUnitCode.QUANTITY}
                  interpretation={1}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.market_share" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">{marketShare}%</span>
              {commonData.isComparisonActive && (
                <MeasureComparison
                  value={parseFloat(marketShare)}
                  comparisonValue={parseFloat(compareShare)}
                  unit={MeasureUnitCode.QUANTITY}
                  interpretation={1}
                />
              )}
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.growth" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">{growth}%</span>
            </div>
          </div>
          <div className="flex flex-col text-left px-6">
            <span className="text-sm font-bold">
              <FormattedMessage id="insights.report.category_ranking" />
            </span>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xl font-bold">#{category.rank}</span>
              {commonData.isComparisonActive && (
                <MeasureComparison
                  value={category.compareRank}
                  comparisonValue={category.rank}
                  unit={MeasureUnitCode.COUNT}
                  interpretation={1}
                />
              )}
            </div>
          </div>
        </div>
        <Tile
          title={ordGmvVal.metric.name}
          additionalTitle={intl.formatMessage({
            id: 'general.title.over_time',
          })}
        >
          <OverTimeChart
            overTimeReport={category}
            timeInfos={commonData.timeInfos}
            maxGmv={commonData.maxGmv}
            maxShare={commonData.maxShare}
            measure={ordGmvVal}
          />
        </Tile>
      </div>
    );
  };

  return (
    <OverTimeReport
      service={Service.Categories}
      testId="categories-over-time-chart"
      extractItems={extractCategoryInfo}
      extractTotals={extractTotals}
      titleId="insights.category.reports.over_time"
      descriptionId="insights.category.reports.over_time.description"
      additionalTitle={intl.formatMessage(
        {
          id: 'general.title.by-metric',
        },
        {
          metric: ordGmvVal.metric.name,
        }
      )}
      renderRow={renderCategoryRow}
    />
  );
}

interface CategoryOverTimeReportInfo extends OverTimeReportInfo {
  code: string;
}
